import { useState } from "react";
import axiosInstance from "../../api/axios";
import toast from "react-hot-toast";
import { Upload, Trash2, FileText, Loader2, RefreshCcw, Calendar } from "lucide-react";

export default function CertificateUploader({ title, certificate, isEnglish, onChange }) {
    const [uploading, setUploading] = useState(false);

    const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        const { data } = await axiosInstance.post("/api/file/upload-admin", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data?.data?.id;
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== "application/pdf") {
            toast.error("فقط فایل PDF مجاز است");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error("حجم فایل نباید بیشتر از ۵ مگابایت باشد");
            return;
        }

        setUploading(true);
        try {
            const fileId = await uploadFile(file);

            if (certificate) {
                await axiosInstance.put("/api/certificate/edit-admin", {
                    certificate_id: certificate.id,
                    file_id: fileId,
                });
                toast.success("گواهینامه‌ها با موفقیت به‌روزرسانی شد");
            } else {
                await axiosInstance.post("/api/certificate/create-admin", {
                    file_id: fileId,
                    is_english: isEnglish,
                });
                toast.success("گواهینامه‌های جدید با موفقیت ایجاد شد");
            }

            onChange();
        } catch (err) {
            toast.error("خطا در بارگذاری فایل");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!certificate) return;
        setUploading(true);
        try {
            await axiosInstance.delete("/api/certificate/delete-list-admin", {
                data: { certificate_ids: [certificate.id] },
            });
            toast.success("گواهینامه‌ها حذف شد");
            onChange();
        } catch {
            toast.error("خطا در حذف گواهینامه‌ها");
        } finally {
            setUploading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return new Intl.DateTimeFormat("fa-IR", {
            dateStyle: "medium",
            timeStyle: "short",
        }).format(date);
    };

    return (
        <div className="relative">
            {uploading && (
                <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center rounded-2xl z-10">
                    <Loader2 className="animate-spin text-accent" size={28} />
                </div>
            )}

            <div
                className={`bg-white/60 backdrop-blur-sm rounded-2xl border border-white/20 p-5 flex flex-col gap-4 transition-opacity ${uploading ? "opacity-60 pointer-events-none" : ""
                    }`}
            >
                <h3 className="font-semibold text-background text-base">{title}</h3>

                {certificate ? (
                    <div className="flex flex-col gap-1">
                        <a
                            href={
                                `${process.env.REACT_APP_BASE_URL}/${certificate?.file?.path}` || "#"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-blue-500 hover:underline"
                        >
                            <FileText size={18} />
                            مشاهده فایل فعلی
                        </a>

                        {certificate.updated_at && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Calendar size={18} />
                                آخرین بروزرسانی: {formatDate(certificate.updated_at)}
                            </div>
                        )}

                        <div className="mt-4 flex items-center gap-3">
                            <label className="flex items-center gap-2 bg-accent text-white px-3 py-2 rounded-lg cursor-pointer text-sm font-medium hover:bg-accent/90">
                                <RefreshCcw size={16} />
                                جایگزینی فایل
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={handleUpload}
                                    className="hidden"
                                    disabled={uploading}
                                />
                            </label>

                            <button
                                onClick={handleDelete}
                                disabled={uploading}
                                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                            >
                                <Trash2 size={16} />
                                حذف
                            </button>
                        </div>
                    </div>
                ) : (
                    <label className="group flex flex-col items-center justify-center border-2 border-dashed border-black/20 hover:bg-orange-100 rounded-xl p-6 text-center cursor-pointer hover:border-accent transition-colors">
                        <Upload size={22} className="text-black/70 group-hover:text-accent mb-2" />
                        <span className="text-sm text-black/70 font-medium">بارگذاری فایل PDF</span>
                        <input
                            type="file"
                            accept="application/pdf"
                            onChange={handleUpload}
                            className="hidden"
                            disabled={uploading}
                        />
                    </label>
                )}
            </div>
        </div>
    );
}